import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { DesignDocument, DesignDocumentRevision } from "../types";

export function useClusterDocuments(clusterId: string) {
  return useQuery({
    queryKey: ["doc-cluster-documents", clusterId],
    queryFn: () =>
      apiClient
        .get<{ data: { results: DesignDocument[] } }>(
          `/project-docs/clusters/${clusterId}/documents/`,
        )
        .then((r) => r.data.data.results),
    staleTime: 30_000,
  });
}

export function useAddDocument(clusterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { title: string; quantity?: number; original_file?: File; notes?: string }) => {
      const fd = new FormData();
      fd.append("title", payload.title);
      if (payload.quantity !== undefined) fd.append("quantity", String(payload.quantity));
      if (payload.original_file) fd.append("original_file", payload.original_file);
      if (payload.notes !== undefined) fd.append("notes", payload.notes);
      return apiClient
        .post<{ data: DesignDocument }>(
          `/project-docs/clusters/${clusterId}/documents/`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        )
        .then((r) => r.data.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster-documents", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
      qc.invalidateQueries({ queryKey: ["doc-awaiting-count"] });
    },
  });
}

export function useEditDocumentNotes(clusterId: string, documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notes: string) =>
      apiClient
        .patch<{ data: DesignDocument }>(
          `/project-docs/clusters/${clusterId}/documents/${documentId}/notes/`,
          { notes },
        )
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster-documents", clusterId] });
    },
  });
}

export function useAddRevision(clusterId: string, documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { original_file?: File; reason?: string }) => {
      const fd = new FormData();
      if (payload.original_file) fd.append("original_file", payload.original_file);
      if (payload.reason) fd.append("reason", payload.reason);
      return apiClient
        .post<{ data: DesignDocumentRevision }>(
          `/project-docs/clusters/${clusterId}/documents/${documentId}/revisions/`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        )
        .then((r) => r.data.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster-documents", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-cluster", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
      qc.invalidateQueries({ queryKey: ["doc-awaiting-count"] });
      qc.invalidateQueries({ queryKey: ["doc-pending-count"] });
    },
  });
}

export function useUploadSignedFile(revisionId: string, clusterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signedFile: File) => {
      const fd = new FormData();
      fd.append("signed_file", signedFile);
      return apiClient
        .patch<{ data: DesignDocumentRevision }>(
          `/project-docs/revisions/${revisionId}/upload-signed/`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" } },
        )
        .then((r) => r.data.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster-documents", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-cluster", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
      qc.invalidateQueries({ queryKey: ["doc-pending-count"] });
      qc.invalidateQueries({ queryKey: ["doc-awaiting-count"] });
      qc.invalidateQueries({ queryKey: ["doc-unacknowledged-count"] });
    },
  });
}

export function useRejectRevision(revisionId: string, clusterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload?: { reason?: string }) =>
      apiClient
        .patch<{ data: DesignDocumentRevision }>(
          `/project-docs/revisions/${revisionId}/reject/`,
          { reason: payload?.reason ?? "" },
        )
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster-documents", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-cluster", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
      qc.invalidateQueries({ queryKey: ["doc-pending-count"] });
      qc.invalidateQueries({ queryKey: ["doc-awaiting-count"] });
    },
  });
}

export function useRejectDocument(clusterId: string, documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload?: { reason?: string }) =>
      apiClient
        .post<{ data: import("../types").DesignDocument }>(
          `/project-docs/clusters/${clusterId}/documents/${documentId}/reject/`,
          { reason: payload?.reason ?? "" },
        )
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster-documents", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
      qc.invalidateQueries({ queryKey: ["doc-cluster", clusterId] });
    },
  });
}

export function useWithdrawDocument(clusterId: string, documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload?: { reason?: string }) =>
      apiClient
        .post<{ data: import("../types").DesignDocument }>(
          `/project-docs/clusters/${clusterId}/documents/${documentId}/withdraw/`,
          { reason: payload?.reason ?? "" },
        )
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster-documents", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
      qc.invalidateQueries({ queryKey: ["doc-cluster", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-awaiting-count"] });
    },
  });
}

export function useApproveDocument(clusterId: string, documentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient
        .post<{ data: import("../types").DesignDocument }>(
          `/project-docs/clusters/${clusterId}/documents/${documentId}/approve/`,
          {},
        )
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster-documents", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
      qc.invalidateQueries({ queryKey: ["doc-cluster", clusterId] });
    },
  });
}

export function useAcknowledgeRevision(revisionId: string, clusterId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient
        .patch<{ data: DesignDocumentRevision }>(
          `/project-docs/revisions/${revisionId}/acknowledge/`,
          {},
        )
        .then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["doc-cluster-documents", clusterId] });
      qc.invalidateQueries({ queryKey: ["doc-clusters"] });
      qc.invalidateQueries({ queryKey: ["doc-unacknowledged-count"] });
    },
  });
}
